import { AnimationCallbackEvent, ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, effect, inject, input, output, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { sideDialogBridgeStyles, sideDialogStyleId } from './side.dialog.theme';

// Page-scoped side panel.
// overlay: WAAPI slide on the panel (absolute — no layout gap).
// push: WAAPI width on the host (transform alone would reserve empty space).
@Component({
  selector: 'wb-side-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './side.dialog.component.html',
  styleUrls: ['./side.dialog.component.less'],
  host: {
    '[class.mode-push]': '$mode() === "push"',
    '[class.is-open]': '$visible()',
  },
})
export class SideDialogComponent {

  private readonly document = inject(DOCUMENT);
  private readonly host = inject(ElementRef<HTMLElement>);

  public $open = input<boolean>(false, { alias: 'open' });
  public $width = input<string>(null, { alias: 'width' });
  public $position = input<'left' | 'right'>('right', { alias: 'position' });
  public $mode = input<'overlay' | 'push'>('overlay', { alias: 'mode' });
  public $backdrop = input<boolean>(false, { alias: 'backdrop' });
  public $canClose = input<(() => boolean) | null>(null, { alias: 'canClose' });

  public closed = output<void>();

  protected readonly $visible = signal(false);

  private openedAt = 0;
  private closing = false;

  public constructor() {

    DesignSystem.Mount(this.document, sideDialogStyleId, sideDialogBridgeStyles);

    effect(() => {

      if (this.$open()) {
        this.closing = false;
        this.openedAt = Date.now();
        this.$visible.set(true);
        return;
      }

      if (this.$visible() && !this.closing) {
        this.closing = true;
        this.$visible.set(false);
      }
    });
  }

  public close(): void {

    const canClose = this.$canClose();
    if (canClose && !canClose()) return;
    if (!this.$visible() || this.closing) return;

    this.closing = true;
    this.$visible.set(false);
  }

  protected onBackdropClick(_event: MouseEvent): void {

    if (Date.now() - this.openedAt < 200) return;
    this.close();
  }

  protected onPanelEnter(event: AnimationCallbackEvent): void {

    void this.runPanelAnimation(event, 'enter');
  }

  protected onPanelLeave(event: AnimationCallbackEvent): void {

    void this.runPanelAnimation(event, 'leave').then(() => {
      this.closing = false;
      this.closed.emit();
    });
  }

  protected onBackdropEnter(event: AnimationCallbackEvent): void {

    void this.runBackdropAnimation(event, 'enter');
  }

  protected onBackdropLeave(event: AnimationCallbackEvent): void {

    void this.runBackdropAnimation(event, 'leave');
  }

  private async runBackdropAnimation(event: AnimationCallbackEvent, phase: 'enter' | 'leave'): Promise<void> {

    try {
      if (this.prefersReducedMotion()) {
        await this.animateElement(event.target as HTMLElement, phase === 'enter' ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], 90);
        return;
      }
      await this.animateElement(event.target as HTMLElement, phase === 'enter' ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], phase === 'enter' ? 180 : 150);
    } finally {
      event.animationComplete();
    }
  }

  private async runPanelAnimation(event: AnimationCallbackEvent, phase: 'enter' | 'leave'): Promise<void> {

    const panel = event.target as HTMLElement;
    const isPush = this.$mode() === 'push';

    try {
      if (this.prefersReducedMotion()) {
        await this.animateElement(panel, phase === 'enter' ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], 90);
        return;
      }

      if (isPush) {
        // Width on host = real layout motion (no empty 440px gap from transform-only).
        await this.animatePush(phase, panel);
        return;
      }

      // overlay — absolute panel, transform is fine (no layout slot).
      const from = this.$position() === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
      await this.animateElement(
        panel,
        phase === 'enter'
          ? [{ transform: from }, { transform: 'translateX(0)' }]
          : [{ transform: 'translateX(0)' }, { transform: from }],
        phase === 'enter' ? 280 : 220,
      );
    } finally {
      event.animationComplete();
    }
  }

  private async animatePush(phase: 'enter' | 'leave', panel: HTMLElement): Promise<void> {

    const hostEl = this.host.nativeElement;
    const targetWidth = this.resolvePushWidth(panel);

    // Host clips; panel stays full width inside so content doesn't reflow mid-anim.
    hostEl.style.overflow = 'hidden';
    panel.style.width = `${targetWidth}px`;
    panel.style.minWidth = `${targetWidth}px`;

    if (phase === 'enter') {
      hostEl.style.width = '0px';
      await this.animateElement(hostEl, [{ width: '0px' }, { width: `${targetWidth}px` }], 280);
      hostEl.style.width = `${targetWidth}px`;
      return;
    }

    // leave — collapse host width (layout), not transform (transform left a blank slot)
    hostEl.style.width = `${hostEl.offsetWidth || targetWidth}px`;
    await this.animateElement(hostEl, [{ width: hostEl.style.width }, { width: '0px' }], 220);
    hostEl.style.width = '';
    hostEl.style.overflow = '';
    panel.style.width = '';
    panel.style.minWidth = '';
  }

  private resolvePushWidth(panel: HTMLElement): number {

    const raw = this.$width();
    if (raw) {
      const px = parseFloat(raw);
      if (!Number.isNaN(px) && px > 0) return px;
    }
    const measured = panel.offsetWidth || panel.getBoundingClientRect().width;
    if (measured > 0) return Math.ceil(measured);
    return 440;
  }

  private async animateElement(element: HTMLElement, keyframes: Keyframe[], duration: number): Promise<void> {

    if (!element?.animate) return;

    const animation = element.animate(keyframes, {
      duration,
      easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
      fill: 'both',
    });
    await animation.finished.catch(() => null);
  }

  private prefersReducedMotion(): boolean {

    return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}
